// hooks/useWebRTC.ts -  WebRTC state management
import { useCallback, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'types/RootState';
import { useCallsSlice } from 'app/pages/Calls/slice';
import { ReceivedCallType } from 'app/pages/Calls/slice/types';
import { receiveError } from 'utils/showSuccessErrorModal';
import fileUpload from 'utils/fileUpload';
import api from 'app/api';

export type CallDirection = 'incoming' | 'outgoing';
export type CallStatus =
  | 'idle'
  | 'calling'
  | 'connected'
  | 'disconnected'
  | 'failed';

export interface CallState {
  status: CallStatus;
  direction: CallDirection | null;
  clientWaNumber: string | null;
  username: string;
  isMuted: boolean;
  callDuration: number;
}

export interface WebRTCReturn {
  // State
  callState: CallState;
  localStream: MediaStream | null;
  isConnected: boolean;
  isRecording: boolean;

  // Actions
  initiateCall: (clientWaNumber: string, username: string) => Promise<void>;
  acceptCall: (call: ReceivedCallType) => Promise<void>;
  rejectCall: (call: ReceivedCallType) => void;
  endCall: () => void;
  toggleMute: () => void;

  // Internal methods (for advanced use)
  handleIncomingSDP: (sdp: string, type: RTCSdpType) => Promise<void>;
}

const DEFAULT_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

//  WebRTC singleton hook
export const useWebRTC = (): WebRTCReturn => {
  const dispatch = useDispatch();
  const { actions } = useCallsSlice();

  const currentCall = useSelector(
    (state: RootState) => state.callsPage?.currentCall,
  );
  const isConnectedFromStore = useSelector(
    (state: RootState) => state.callsPage?.isConnected,
  );
  const permission = useSelector(
    (state: RootState) => state.authPage?.permission,
  );

  // Refs for WebRTC objects
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const callTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Recording refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const currentCallWamidRef = useRef<string | null>(null); // Store wamid for API calls
  const [isRecording, setIsRecording] = useState<boolean>(false);

  // Local state
  const [callState, setCallState] = useState<CallState>({
    status: 'idle',
    direction: null,
    clientWaNumber: null,
    isMuted: false,
    callDuration: 0,
    username: '',
  });

  // Create single remote audio element
  useEffect(() => {
    if (!remoteAudioRef.current) {
      const audio = document.createElement('audio');
      audio.autoplay = true;
      audio.style.display = 'none';
      document.body.appendChild(audio);
      remoteAudioRef.current = audio;
    }

    return () => {
      if (remoteAudioRef.current) {
        document.body.removeChild(remoteAudioRef.current);
        remoteAudioRef.current = null;
      }
    };
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    console.log('🧹 Cleaning up WebRTC resources...');

    // Stop call timer
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }

    // Stop recording if active (with a small delay to allow data collection)
    if (mediaRecorderRef.current) {
      console.log('🎙️ Stopping recording...');
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
      setIsRecording(false);
    }

    // Stop local stream tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`Stopped ${track.kind} track`);
      });
      localStreamRef.current = null;
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Clear remote audio
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }

    // Reset state
    setCallState({
      status: 'idle',
      direction: null,
      clientWaNumber: null,
      isMuted: false,
      callDuration: 0,
      username: '',
    });

    // Update Redux store
    dispatch(actions.setIsConnected(false));
  }, [dispatch, actions]); // Removed isRecording dependency to prevent cycles

  // Upload recording to S3
  const uploadRecordingToS3 = useCallback(async () => {
    if (recordedChunksRef.current.length === 0) {
      console.warn('⚠️ No recorded chunks to upload');
      return;
    }

    try {
      const recordedBlob = new Blob(recordedChunksRef.current, {
        type: 'audio/webm;codecs=opus',
      });

      // Create file with timestamp and call info
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `call-recording-${callState.username}-${timestamp}.webm`;
      const recordingFile = new File([recordedBlob], fileName, {
        type: 'audio/webm;codecs=opus',
      });

      console.log('📤 Uploading call recording to S3...');
      const uploadResult = await fileUpload(recordingFile);

      if (uploadResult && uploadResult.url) {
        // Use stored wamid for API call since currentCall might be null
        const wamid = currentCallWamidRef.current;
        if (wamid) {
          await api.put(`messages/${wamid}`, {
            name: fileName,
            awsLink: uploadResult.url,
            wamid: wamid,
          });
          console.log('✅ Call recording API updated successfully');
        } else {
          console.warn('⚠️ No wamid available for API call');
        }

        console.log(
          '✅ Call recording uploaded successfully:',
          uploadResult.url,
        );
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('❌ Error uploading recording:', error);
      receiveError('Failed to upload call recording');
    } finally {
      // Clear recorded chunks
      recordedChunksRef.current = [];
      currentCallWamidRef.current = null; // Clear wamid reference
    }
  }, [callState.username]);

  // Start call recording
  const startRecording = useCallback(async () => {
    if (!localStreamRef.current || !remoteAudioRef.current?.srcObject) {
      console.warn('⚠️ Cannot record: Local or remote stream is missing.');
      return;
    }

    try {
      console.log('🎙️ Merging audio streams for high-quality recording...');
      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();

      // Add local microphone audio to the mix
      const localSource = audioContext.createMediaStreamSource(
        localStreamRef.current,
      );
      localSource.connect(destination);

      // Add remote user's audio to the mix
      const remoteStream = remoteAudioRef.current.srcObject as MediaStream;
      const remoteSource = audioContext.createMediaStreamSource(remoteStream);
      remoteSource.connect(destination);

      const streamToRecord = destination.stream;
      const recorder = new MediaRecorder(streamToRecord, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000, // Higher bitrate for better quality
      });

      recordedChunksRef.current = [];
      recorder.ondataavailable = event => {
        if (event.data.size > 0) recordedChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        console.log('🎙️ Recording stopped, preparing to upload...');
        uploadRecordingToS3();
        setIsRecording(false);
        audioContext.close(); // Clean up the audio context
      };

      recorder.start(1000); // Collect data in 1-second chunks
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      console.log('✅ Call recording started with combined audio.');
    } catch (error) {
      console.error('❌ Error starting recording:', error);
      receiveError('Failed to start call recording.');
    }
  }, [uploadRecordingToS3]);

  // Initialize peer connection
  const initializePeerConnection = useCallback(
    async (direction: CallDirection): Promise<RTCPeerConnection> => {
      if (peerConnectionRef.current) {
        return peerConnectionRef.current;
      }

      const pc = new RTCPeerConnection({ iceServers: DEFAULT_ICE_SERVERS });

      pc.ontrack = event => {
        console.log('📡 Received remote track:', event);
        if (remoteAudioRef.current && event.streams[0]) {
          remoteAudioRef.current.srcObject = event.streams[0];
        }
      };

      pc.onicecandidate = event => {
        if (event.candidate) {
          console.log('🧊 ICE candidate:', event.candidate);
          // You can dispatch ICE candidates to your signaling server here
        }
      };

      pc.onconnectionstatechange = () => {
        console.log('🔄 Connection state:', pc.connectionState);
        console.log('📞 Call direction:', direction);

        const isConnected = pc.connectionState === 'connected';

        // Only dispatch connected action for incoming calls
        if (direction === 'incoming') {
          dispatch(actions.setIsConnected(isConnected));
        }

        if (isConnected) {
          setCallState(prev => ({ ...prev, status: 'connected' }));
          console.log('✅ Call connected, direction:', direction);

          // Start call duration timer only for incoming calls
          // (outgoing calls timer is handled in useEffect)
          if (direction === 'incoming' && !callTimerRef.current) {
            callTimerRef.current = setInterval(() => {
              setCallState(prev => ({
                ...prev,
                callDuration: prev.callDuration + 1,
              }));
            }, 1000);

            // Start recording for incoming calls if permission is granted
            if (permission?.callRecording) {
              console.log('🎙️ Starting recording for incoming call');
              startRecording();
            } else {
              console.log('⚠️ Call recording permission not granted');
            }
          }
        } else if (
          pc.connectionState === 'disconnected' ||
          pc.connectionState === 'failed'
        ) {
          console.log('🔄 Connection state changed to:', pc.connectionState);
          setCallState(prev => ({ ...prev, status: 'disconnected' }));
          // Don't auto-cleanup here - let Redux and the useEffect handle cleanup
          console.log(
            '🔄 Connection failed/disconnected, waiting for Redux cleanup...',
          );
        }
      };

      peerConnectionRef.current = pc;
      return pc;
    },
    [dispatch, actions, permission?.callRecording, startRecording],
  );

  // Get user media
  // const getUserMedia = useCallback(async (): Promise<MediaStream> => {
  //   try {
  //     const stream = await navigator.mediaDevices.getUserMedia({
  //       audio: true,
  //       video: false,
  //     });
  //     localStreamRef.current = stream;
  //     return stream;
  //   } catch (error) {
  //     console.error('❌ Error getting user media:', error);
  //     throw error;
  //   }
  // }, []);

  const getUserMedia = useCallback(async (): Promise<MediaStream> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
        },
        video: false,
      });
      localStreamRef.current = stream;
      return stream;
    } catch (error) {
      console.error('❌ Error getting user media:', error);
      throw error;
    }
  }, []);

  // Initiate outgoing call
  const initiateCall = useCallback(
    async (clientWaNumber: string, username: string) => {
      if (callState.status !== 'idle') {
        receiveError('❌ Cannot initiate call. Another call is in progress.');
        console.error('❌ Cannot initiate call. Another call is in progress.');
        return;
      }

      console.log('📞 Initiating call to:', clientWaNumber);

      setCallState(prev => ({
        ...prev,
        status: 'calling',
        direction: 'outgoing',
        clientWaNumber,
        username,
      }));

      try {
        const stream = await getUserMedia();
        const pc = await initializePeerConnection('outgoing');

        // Add stream to peer connection
        stream.getTracks().forEach(track => pc.addTrack(track, stream));

        // Create offer
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        // Dispatch to Redux and API
        const refId = Date.now().toString(); // Simple ref ID

        dispatch(
          actions.initiateCallStart({
            clientWaNumber,
            username,
            session: { sdpType: 'offer', sdp: offer.sdp || '' },
            refId,
          }),
        );

        // You can add API call here
        console.log('📤 Sent call offer for:', clientWaNumber);
      } catch (error) {
        receiveError(error);
        console.error('❌ Error initiating call:', error);
        setCallState(prev => ({ ...prev, status: 'failed' }));
        cleanup();
        throw error;
      }
    },
    [
      callState.status,
      getUserMedia,
      initializePeerConnection,
      dispatch,
      actions,
      cleanup,
    ],
  );

  // Accept incoming call
  const acceptCall = useCallback(
    async (selectedCall: ReceivedCallType) => {
      if (callState.status !== 'idle') {
        receiveError('❌ Cannot accept call. Another call is in progress.');
        console.error('❌ Cannot accept call. Another call is in progress.');
        return;
      }

      console.log('✅ Accepting call from:', selectedCall.clientWaNumber);

      // Store wamid for later API calls (persists even after currentCall becomes null)
      currentCallWamidRef.current = selectedCall.message.wamid;
      console.log('💾 Stored wamid for recording:', selectedCall.message.wamid);

      setCallState(prev => ({
        ...prev,
        status: 'calling',
        direction: 'incoming',
        clientWaNumber: selectedCall.clientWaNumber,
        username: selectedCall.username,
      }));

      try {
        const stream = await getUserMedia();
        const pc = await initializePeerConnection('incoming');

        // Add stream to peer connection
        stream.getTracks().forEach(track => pc.addTrack(track, stream));

        // Handle incoming SDP offer
        if (
          selectedCall.session?.sdpType === 'offer' &&
          selectedCall.session.sdp
        ) {
          const sessionDesc = new RTCSessionDescription({
            type: 'offer',
            sdp: selectedCall.session.sdp,
          });

          await pc.setRemoteDescription(sessionDesc);

          // Create answer
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);

          // Dispatch answer
          dispatch(
            actions.preAcceptCallStart({
              wacid: selectedCall.message.wamid,
              session: {
                sdpType: 'answer',
                sdp: answer.sdp || '',
              },
              clientWaNumber: selectedCall.clientWaNumber || '',
              username: selectedCall.username || selectedCall.clientWaNumber,
            }),
          );
        }
      } catch (error) {
        console.error('❌ Error accepting call:', error);
        setCallState(prev => ({ ...prev, status: 'failed' }));
        cleanup();
        throw error;
      }
    },
    [
      callState.status,
      getUserMedia,
      initializePeerConnection,
      dispatch,
      actions,
      cleanup,
    ],
  );

  // Reject incoming call
  const rejectCall = useCallback(
    (call: ReceivedCallType) => {
      console.log('❌ Rejecting call from:', call.clientWaNumber);

      dispatch(
        actions.rejectCallStart({
          wacid: call.message.wamid,
          clientWaNumber: call.clientWaNumber || '',
        }),
      );
    },
    [dispatch, actions],
  );

  // End current call
  const endCall = useCallback(() => {
    console.log('📞 Ending call...');

    if (currentCall) {
      dispatch(
        actions.terminateCallStart({
          wacid: currentCall.message?.wamid || '',
          clientWaNumber: callState.clientWaNumber || '',
        }),
      );
    }

    cleanup();
  }, [currentCall, callState.clientWaNumber, dispatch, actions, cleanup]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) {
      console.warn('⚠️ No local stream available for mute toggle');
      return;
    }

    const audioTracks = localStreamRef.current.getAudioTracks();
    if (audioTracks.length === 0) {
      console.warn('⚠️ No audio tracks found');
      return;
    }

    setCallState(prev => {
      const newMutedState = !prev.isMuted;
      audioTracks.forEach(track => {
        track.enabled = !newMutedState;
        console.log(`🎤 Audio track ${newMutedState ? 'muted' : 'unmuted'}`);
      });
      return { ...prev, isMuted: newMutedState };
    });
  }, []);

  // Handle incoming SDP (for answers to outgoing calls)
  const handleIncomingSDP = useCallback(
    async (sdp: string, type: RTCSdpType) => {
      const pc = peerConnectionRef.current;
      if (!pc) {
        console.error('❌ No peer connection available for SDP');
        return;
      }

      try {
        const sessionDescription = new RTCSessionDescription({ type, sdp });
        await pc.setRemoteDescription(sessionDescription);
        console.log('✅ Successfully set remote SDP:', type);
      } catch (error) {
        console.error('❌ Error setting remote SDP:', error);
        setCallState(prev => ({ ...prev, status: 'failed' }));
        cleanup();
      }
    },
    [cleanup],
  );

  // Handle call timer when connected for outgoing calls
  useEffect(() => {
    if (
      isConnectedFromStore &&
      currentCall?.direction === 'BUSINESS_INITIATED' &&
      !callTimerRef.current
    ) {
      console.log('📞 Starting call timer for outgoing call');
      setCallState(prev => ({ ...prev, status: 'connected' }));

      // Start call duration timer
      callTimerRef.current = setInterval(() => {
        setCallState(prev => ({
          ...prev,
          callDuration: prev.callDuration + 1,
        }));
      }, 1000);

      // Start recording when call connects for outgoing calls
      if (permission?.callRecording) {
        startRecording();
      }
    }
  }, [
    isConnectedFromStore,
    currentCall?.direction,
    permission?.callRecording,
    startRecording,
  ]);

  // Separate effect for cleanup to avoid premature triggering
  useEffect(() => {
    // Only cleanup if both conditions are false (call truly ended)
    if (!isConnectedFromStore && !currentCall) {
      console.log('🔄 Call ended, running cleanup...');
      const timeoutId = setTimeout(() => {
        cleanup();
      }, 1000); // Delay cleanup by 1 second to allow recording to finish

      return () => clearTimeout(timeoutId);
    }
  }, [isConnectedFromStore, currentCall, cleanup]);

  // Handle incoming SDP answers from Redux store
  useEffect(() => {
    const session = currentCall?.session;
    if (
      session?.sdpType === 'answer' &&
      session.sdp &&
      callState.direction === 'outgoing'
    ) {
      currentCallWamidRef.current = currentCall?.message?.wamid || null;
      handleIncomingSDP(session.sdp, 'answer');
    }
  }, [
    currentCall?.session,
    callState.direction,
    handleIncomingSDP,
    currentCall?.message?.wamid,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    callState,
    localStream: localStreamRef.current,
    isConnected: isConnectedFromStore || false,
    isRecording,
    initiateCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    handleIncomingSDP,
  };
};
